import { ActionIcon, Box, Menu, Popover } from '@mantine/core';
import { IconGridDots } from '@tabler/icons';
import { useAppSelector } from '@/hooks/redux/redux';
import { getMenu } from './utils';
import { ModuleSettings } from '@/entities/admin-app/auth';
import { useStyles } from './styles';
import { NoImage } from '@/assets/icons';
import { themeColors } from '@/theme/colors';
import { TailType } from '@/services/api/menuTailsApiArea/types';
import React from 'react';
import { IconLayersLinked } from '@tabler/icons-react';
import LinksDialog from '@components/apps-menu/LinksDialog';
import { useTranslation } from 'react-i18next';

export const AppsMenu = () => {
  const { t } = useTranslation();
  const { classes } = useStyles();
  const [linksOpen, setLinksOpen] = React.useState(false);
  const closeTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleLinksEnter = React.useCallback(() => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
    setLinksOpen(true);
  }, []);

  const handleLinksLeave = React.useCallback(() => {
    closeTimerRef.current = setTimeout(() => setLinksOpen(false), 150);
  }, []);

  const modules = useAppSelector(
    (state) => state.userInfo.Settings?.ModuleSettings
  ) as ModuleSettings;
  const menu = modules ? getMenu(modules) : [];

  if (!menu) {
    return null;
  }

  const modulesItems = menu.filter(
    (i) => i.tailType === TailType.Module || i.tailType === TailType.Integration
  );

  const hasLinks = menu.some((i) => i.tailType === TailType.Link);
  const links = menu.filter((i) => i.tailType === TailType.Link);

  return (
    <Menu
      position="bottom-end"
      shadow="sm"
      zIndex={1001}
      radius={8}
      classNames={{
        dropdown: classes.dropdown,
        item: classes.menuItem,
        itemLabel: classes.itemLabel,
        itemSection: classes.itemIcon
      }}
    >
      <Menu.Target>
        <ActionIcon size={26}>
          <IconGridDots size={26} />
        </ActionIcon>
      </Menu.Target>
      <Menu.Dropdown w={260}>
        {modulesItems
          .filter((item) => item.label)
          .map((item, index) => (
            <Menu.Item
              key={index}
              component="a"
              href={item.url || undefined}
              fz={16}
              px="sm"
              my="4px"
              target="_blank"
              leftSection={
                item.iconSrc ? (
                  <Box component="img" src={item.iconSrc} height={24} />
                ) : (
                  <NoImage stroke={themeColors.brandGray[0]} />
                )
              }
            >
              {item.label}
            </Menu.Item>
          ))}
        {hasLinks && (
          <Popover
            opened={linksOpen}
            position="left-start"
            withinPortal
            zIndex={1002}
            shadow="xs"
            offset={{ mainAxis: 10, crossAxis: 0 }}
          >
            <Popover.Target>
              <div
                style={{ width: '100%' }}
                onMouseEnter={handleLinksEnter}
                onMouseLeave={handleLinksLeave}
              >
                <Menu.Item
                  fz={16}
                  px="sm"
                  w="100%"
                  my="4px"
                  leftSection={<IconLayersLinked stroke={themeColors.brandGray[0]} />}
                >
                  {t('appsMenu.links')}
                </Menu.Item>
              </div>
            </Popover.Target>
            <Popover.Dropdown
              style={{ padding: 0, maxWidth: 250 }}
              onMouseEnter={handleLinksEnter}
              onMouseLeave={handleLinksLeave}
            >
              <LinksDialog links={links} />
            </Popover.Dropdown>
          </Popover>
        )}
      </Menu.Dropdown>
    </Menu>
  );
};
